import { useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { MRT_ColumnFiltersState, MRT_SortingState } from "material-react-table";
import {
  PAGE_INDEX_QUERY_NAME,
  PAGE_SIZE_QUERY_NAME,
  SEARCH_QUERY_NAME,
  SORT_QUERY_NAME,
  PAGE_SIZE_QUERY_ALL_VALUE,
  INITIAL_PAGE_SIZE_QUERY_VALUE,
  PAGE_SIZE_QUERY_NAME_ALL,
  TABS_QUERY_NAME,
  ASCENDING_NAME,
  DESCENDING_NAME,
} from "@techzyapp/ui-lib";

interface UseTableFilterProps {
  initialPage: number;
  initialSize: number;
  initialSearch: string;
  initialSort: MRT_SortingState;
  initialFilters: MRT_ColumnFiltersState;
  setPagination: (pageIndex: number, pageSize: number) => void;
  setSearch: (key: string) => void;
  setSort: (sorting: MRT_SortingState) => void;
  setFilters: (filters: MRT_ColumnFiltersState) => void;
}

export const useTableFilter = (): UseTableFilterProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialPage = Number(searchParams.get(PAGE_INDEX_QUERY_NAME)) || 1;
  const initialSize =
    searchParams.get(PAGE_SIZE_QUERY_NAME) === PAGE_SIZE_QUERY_NAME_ALL
      ? PAGE_SIZE_QUERY_ALL_VALUE
      : Number(searchParams.get(PAGE_SIZE_QUERY_NAME)) ||
        INITIAL_PAGE_SIZE_QUERY_VALUE;
  const initialSearch = searchParams.get(SEARCH_QUERY_NAME) || "";
  const initialSort: MRT_SortingState = (() => {
    const sort = searchParams.get(SORT_QUERY_NAME);
    if (!sort) return [];

    const [column, direction] = sort.split(
      new RegExp(`(${ASCENDING_NAME}|${DESCENDING_NAME})$`)
    );
    return direction
      ? [{ id: column, desc: direction === DESCENDING_NAME }]
      : [];
  })();
  const initialFilters: MRT_ColumnFiltersState = Array.from(
    searchParams.entries()
  )
    .filter(
      ([key]) =>
        ![
          PAGE_INDEX_QUERY_NAME,
          PAGE_SIZE_QUERY_NAME,
          SEARCH_QUERY_NAME,
          TABS_QUERY_NAME,
          SORT_QUERY_NAME,
        ].includes(key)
    )
    .reduce<MRT_ColumnFiltersState>((acc, [key, value]) => {
      const match = key.match(/^(.+)\[(\d+)\]$/);
      if (match) {
        const [, baseKey, index] = match;
        let filter = acc.find((filter) => filter.id === baseKey);

        if (!filter) {
          const isStringArray = value && isNaN(Number(value));

          filter = {
            id: baseKey,
            value: isStringArray ? ([] as string[]) : ([] as number[]),
          };
          acc.push(filter);
        }

        if (Array.isArray(filter.value)) {
          if (isNaN(Number(value))) {
            (filter.value as string[])[Number(index)] = value;
          } else {
            (filter.value as number[])[Number(index)] = Number(value);
          }
        }
      } else {
        let parsedValue: string | number = isNaN(Number(value))
          ? value
          : Number(value);
        acc.push({
          id: key,
          value: parsedValue,
        });
      }

      return acc;
    }, []);

  useEffect(() => {
    if (
      !searchParams.has(PAGE_INDEX_QUERY_NAME) &&
      !searchParams.has(PAGE_SIZE_QUERY_NAME)
    ) {
      searchParams.set(PAGE_INDEX_QUERY_NAME, "1");
      searchParams.set(
        PAGE_SIZE_QUERY_NAME,
        INITIAL_PAGE_SIZE_QUERY_VALUE.toString()
      );
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );
    }
  }, [searchParams, navigate]);

  const setPagination = (pageIndex: number, pageSize: number | string) => {
    pageSize === PAGE_SIZE_QUERY_ALL_VALUE
      ? searchParams.delete(PAGE_INDEX_QUERY_NAME)
      : searchParams.set(PAGE_INDEX_QUERY_NAME, `${pageIndex}`);

    searchParams.set(
      PAGE_SIZE_QUERY_NAME,
      pageSize === PAGE_SIZE_QUERY_ALL_VALUE
        ? PAGE_SIZE_QUERY_NAME_ALL
        : `${pageSize}`
    );

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const setSearch = (key: string) => {
    if (key) searchParams.set(SEARCH_QUERY_NAME, key);
    else searchParams.delete(SEARCH_QUERY_NAME);

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const setSort = (sorting: MRT_SortingState) => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      const sortDirection = sort.desc ? DESCENDING_NAME : ASCENDING_NAME;
      const sortBy = `${sort.id}${sortDirection}`;
      searchParams.set(SORT_QUERY_NAME, sortBy);
    } else {
      searchParams.delete(SORT_QUERY_NAME);
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const setFilters = (filters: MRT_ColumnFiltersState) => {
    Array.from(searchParams.keys()).forEach((key) => {
      if (
        key !== PAGE_INDEX_QUERY_NAME &&
        key !== PAGE_SIZE_QUERY_NAME &&
        key !== SEARCH_QUERY_NAME &&
        key !== TABS_QUERY_NAME &&
        key !== SORT_QUERY_NAME
      ) {
        searchParams.delete(key);
      }
    });

    filters.forEach((filter) => {
      const { id, value } = filter;

      if (Array.isArray(value)) {
        value.forEach((v, index) => {
          searchParams.set(`${id}[${index}]`, String(v));
        });
      } else {
        searchParams.set(id, String(value));
      }
    });

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  return {
    initialPage,
    initialSize,
    initialSearch,
    initialSort,
    initialFilters,
    setPagination,
    setSearch,
    setSort,
    setFilters,
  };
};

export default useTableFilter;
